/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reatomMap, withAssign } from '@reatom/framework'
import * as DateF from 'date-fns'

import { AsyncStateN, Brand } from '@blade/std-lib'
import { rootCtx } from '@root/shared/lib/reatom/root-ctx'

import { reatomPathfinderQuoteSubscription } from './reatom-quote-subscription'

import type { PathfinderQuoteSubscriptionModel } from './reatom-quote-subscription'
import type { CtxSpy } from '@reatom/framework'
import type { PairModel } from '@root/entities/pair/atoms'
import type { SwapDirection } from '@root/entities/trade-type/model/trade-type'
import type { BigNumber } from 'bignumber.js'

type QuoteRegistryKey = Brand.Branded<string, 'QuoteRegistryKey'>
// eslint-disable-next-line @typescript-eslint/no-redeclare
const QuoteRegistryKey = Brand.nominal<QuoteRegistryKey>()

export type QuoteRegistryKeyParams = {
  pair: PairModel
  amount: BigNumber
  type: SwapDirection
}

export const quoteRegistryKey = ({
  pair,
  amount,
  type,
}: QuoteRegistryKeyParams): QuoteRegistryKey =>
  QuoteRegistryKey(`${pair.quote.ticker}:${amount.toString()}:${type}`)

type Entry = {
  model: PathfinderQuoteSubscriptionModel
  subscribers: Set<string>
  params: QuoteRegistryKeyParams
}

export const reatomQuoteSubscriptionRegistry = () => {
  const entriesAtom = reatomMap<QuoteRegistryKey, Entry>()

  const setAction = action(
    (
      ctx,
      params: QuoteRegistryKeyParams,
      subscriberKey: string,
      onCreated: (model: PathfinderQuoteSubscriptionModel) => void,
    ): PathfinderQuoteSubscriptionModel => {
      const key = quoteRegistryKey(params)
      const existing = entriesAtom.get(ctx, key)
      if (existing) {
        existing.subscribers.add(subscriberKey)
        return existing.model
      }
      const model = reatomPathfinderQuoteSubscription({
        pair: params.pair,
        amount: params.amount,
        type: params.type,
      })
      entriesAtom.set(ctx, key, {
        model,
        subscribers: new Set([subscriberKey]),
        params,
      })
      onCreated(model)
      return model
    },
  )

  const removeAction = action(
    (
      ctx,
      params: QuoteRegistryKeyParams,
      subscriberKey: string,
      onEvicted: (model: PathfinderQuoteSubscriptionModel) => void,
    ) => {
      const key = quoteRegistryKey(params)
      const entry = entriesAtom.get(ctx, key)
      if (!entry) {
        return
      }

      entry.subscribers.delete(subscriberKey)
      if (entry.subscribers.size === 0) {
        entriesAtom.delete(ctx, key)
        onEvicted(entry.model)
      }
    },
    'removeAction',
  )

  const entriesReadonlyAtom = atom((ctx) => {
    return ctx.spy(entriesAtom)
  })

  const getAction = action((ctx, params: QuoteRegistryKeyParams) => {
    const key = quoteRegistryKey(params)
    const entry = ctx.get(entriesAtom).get(key)
    if (!entry) {
      return undefined
    }
    return ctx.get(entry.model).value?.quote
  })

  const watch = (ctx: CtxSpy, params: QuoteRegistryKeyParams) => {
    const key = quoteRegistryKey(params)
    const entry = ctx.spy(entriesAtom).get(key)
    if (!entry) {
      return undefined
    }
    return entry.model
  }

  return entriesReadonlyAtom.pipe(
    withAssign(() => ({
      remove: removeAction,
      set: setAction,
      get: getAction,
      entries: entriesReadonlyAtom,
      watch,
    })),
  )
}

export const quoteSubscriptionRegistry = reatomQuoteSubscriptionRegistry()

declare global {
  interface Window {
    __PRINT_QUOTES: () => void
  }
}

window.__PRINT_QUOTES = () => {
  const ctx = rootCtx
  // `console.table` can't set column widths — each column auto-fits its widest
  // cell (header or value), so `sub`/`amt` are kept short (and the boolean is
  // rendered as Y/N) to keep those columns narrow.
  const rows: Record<
    string,
    {
      address: string
      symbol: string
      amount: string
      subscribed: string
      firstQuoteReceivedAt: string | undefined
      subscribedAt: string | undefined
      waitTime: string
      error: string | undefined
    }
  > = {}

  for (const [key, entry] of ctx.get(quoteSubscriptionRegistry.entries)) {
    const state = ctx.get(entry.model)

    let error: string | undefined
    if (AsyncStateN.isError(state)) {
      const err = state.error
      error =
        err._tag === 'UnknownError' && err.message
          ? `${err._tag}: ${err.message}`
          : err._tag
    }

    const firstQuoteReceivedAt = ctx.get(entry.model.firstQuoteReceivedAt)
    const subscribedAt = ctx.get(entry.model.subscribedAt)
    const firstQuoteReceivedAtFormatted = firstQuoteReceivedAt
      ? DateF.format(firstQuoteReceivedAt, 'HH:mm:ss.SSS')
      : undefined
    const subscribedAtFormatted = subscribedAt
      ? DateF.format(subscribedAt, 'HH:mm:ss.SSS')
      : undefined

    const waitTime =
      firstQuoteReceivedAt && subscribedAt
        ? `${firstQuoteReceivedAt.getTime() - subscribedAt.getTime()}ms`
        : '-'

    rows[key] = {
      address: entry.params.pair.quote.address,
      symbol: entry.params.pair.quote.ticker,
      amount: entry.params.amount.toString(),
      subscribed: ctx.get(entry.model.hasSubscription) ? '🟢' : '🔴',
      subscribedAt: subscribedAtFormatted,
      firstQuoteReceivedAt: firstQuoteReceivedAtFormatted,
      waitTime,
      error,
    }
  }

  // eslint-disable-next-line no-console
  console.table(rows)
}
